import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { api } from '../utils/api';
import { SERVER_HOST } from '../utils/apiBase';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [inQueue, setInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const [matchFound, setMatchFound] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);

  useEffect(() => {
    // Get auth token
    const token = api.getStoredUser()?.token;
    
    if (!token) {
      console.log('No auth token, socket not initialized');
      return;
    }

    // Connect to Socket.io server. SERVER_HOST is '' in web prod (same-origin),
    // an absolute https://www.ranga.com in Capacitor, or localhost in dev. The
    // socket.io client treats '' as same-origin, which is the correct prod web
    // behavior.
    const SERVER_URL = SERVER_HOST || undefined;
    const newSocket = io(SERVER_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ Socket disconnected');
      setConnected(false);
      setInQueue(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setConnected(false);
    });

    // Queue events
    newSocket.on('queue_joined', (data) => {
      console.log('Joined queue, position:', data.position);
      setInQueue(true);
      setQueuePosition(data.position);
    });

    newSocket.on('queue_left', () => {
      console.log('Left queue');
      setInQueue(false);
      setQueuePosition(null);
    });

    newSocket.on('queue_error', (data) => {
      console.error('Queue error:', data.message);
      alert(data.message);
    });

    // Match found
    newSocket.on('match_found', (data) => {
      console.log('🎯 Match found!', data);
      setInQueue(false);
      setMatchFound(data);
      setGameStarted(true);
    });

    setSocket(newSocket);

    return () => {
      console.log('Cleaning up socket connection');
      newSocket.close();
    };
  }, []);

  const joinQueue = (rating = 1200) => {
    if (!socket || !connected) {
      console.error('Socket not connected');
      return;
    }
    
    console.log('Joining matchmaking queue...');
    socket.emit('join_queue', { rating });
  };

  const leaveQueue = () => {
    if (!socket || !connected) {
      return;
    }
    
    console.log('Leaving matchmaking queue...');
    socket.emit('leave_queue');
  };

  const makeMove = (gameId, move) => {
    if (!socket || !connected) {
      console.error('Socket not connected');
      return;
    }
    
    socket.emit('make_move', { gameId, move });
  };

  const sendGameOver = (gameId, winner, reason) => {
    if (!socket || !connected) {
      return;
    }
    
    socket.emit('game_over', { gameId, winner, reason });
  };

  const sendChatMessage = (gameId, message) => {
    if (!socket || !connected) {
      return;
    }
    
    socket.emit('chat_message', { gameId, message });
  };

  const resetMatch = () => {
    setMatchFound(null);
    setGameStarted(false);
  };

  const value = {
    socket,
    connected,
    inQueue,
    queuePosition,
    matchFound,
    gameStarted,
    joinQueue,
    leaveQueue,
    makeMove,
    sendGameOver,
    sendChatMessage,
    resetMatch
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
